import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Section, Block, SectionAlignment, SectionLayout, SectionTheme } from "./types";
import { SortableBlock } from "./BlockComponents/SortableBlock";
import { AddBlockDialog } from "./components/AddBlockDialog";
import { SectionBackgroundDialog } from "./SectionBackgroundDialog";
import { AlignStartHorizontal, AlignEndHorizontal, AlignCenterHorizontal, AlignStartVertical, AlignEndVertical, AlignCenterVertical } from "lucide-react";
import { cn } from "@/lib/utils";
interface SectionEditorProps {
  section: Section;
  onUpdate: (section: Section) => void;
  onDelete: () => void;
  isPro?: boolean;
}
export const SectionEditor = ({
  section,
  onUpdate,
  onDelete,
  isPro = false
}: SectionEditorProps) => {
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [isBackgroundDialogOpen, setIsBackgroundDialogOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  const addBlock = (type: Block["type"]) => {
    const newBlock: Block = {
      id: crypto.randomUUID(),
      type,
      content: ""
    };
    if (type === "posts") {
      newBlock.postsConfig = {
        title: "Latest Posts",
        postsPerPage: 6
      };
    }
    onUpdate({
      ...section,
      blocks: [...section.blocks, newBlock]
    });
    setIsBlockDialogOpen(false);
  };
  const updateBlock = (id: string, updates: Partial<Block>) => {
    onUpdate({
      ...section,
      blocks: section.blocks.map(block => block.id === id ? {
        ...block,
        ...updates
      } : block)
    });
  };
  const removeBlock = (id: string) => {
    onUpdate({
      ...section,
      blocks: section.blocks.filter(block => block.id !== id)
    });
  };
  const handleDragEnd = (event: any) => {
    const {
      active,
      over
    } = event;
    if (active.id !== over.id) {
      const oldIndex = section.blocks.findIndex(block => block.id === active.id);
      const newIndex = section.blocks.findIndex(block => block.id === over.id);
      onUpdate({
        ...section,
        blocks: arrayMove(section.blocks, oldIndex, newIndex)
      });
    }
  };
  const updateAlignment = (type: 'horizontal' | 'vertical', value: any) => {
    onUpdate({
      ...section,
      alignment: {
        ...(section.alignment || {
          horizontal: 'center',
          vertical: 'center'
        }),
        [type]: value
      }
    });
  };
  return <div className="space-y-6">
      <Tabs defaultValue="blocks" className="w-full">
        <TabsList className="w-full grid grid-cols-2">
          <TabsTrigger value="blocks">Blocks</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="blocks" className="space-y-4">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={section.blocks.map(block => block.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-4 w-full">
                {section.blocks.map(block => <SortableBlock key={block.id} block={block} onUpdate={updates => updateBlock(block.id, updates)} onDelete={() => removeBlock(block.id)} />)}
              </div>
            </SortableContext>
          </DndContext>

          <AddBlockDialog isOpen={isBlockDialogOpen} onOpenChange={setIsBlockDialogOpen} onAddBlock={addBlock} />

          <Button variant="outline" size="sm" onClick={() => setIsBlockDialogOpen(true)} className="w-full h-9 text-sm text-[#8E9196] hover:text-slate-900 hover:bg-slate-100 border border-input rounded-full">
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Block
          </Button>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Section Name</Label>
              <Input value={section.name || ""} onChange={e => onUpdate({
              ...section,
              name: e.target.value
            })} placeholder="Enter section name" />
            </div>

            <div className="space-y-2">
              <Label>Layout</Label>
              <Select value={section.layout} onValueChange={(value: SectionLayout) => onUpdate({
              ...section,
              layout: value
            })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select layout" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-col">1 Column</SelectItem>
                  <SelectItem value="2-col">2 Columns</SelectItem>
                  <SelectItem value="3-col">3 Columns</SelectItem>
                  <SelectItem value="4-col">4 Columns</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Theme</Label>
              <Select value={section.theme} onValueChange={(value: SectionTheme) => onUpdate({
              ...section,
              theme: value
            })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select theme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="primary">Primary</SelectItem>
                  <SelectItem value="secondary">Secondary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Background</Label>
              <Button variant="outline" className="w-full" onClick={() => setIsBackgroundDialogOpen(true)}>
                {section.background?.image ? "Change Background" : "Add Background"}
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Horizontal Alignment</Label>
              <div className="flex gap-2">
                <Button variant={section.alignment?.horizontal === "start" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => updateAlignment('horizontal', 'start')}>
                  <AlignStartHorizontal className="w-4 h-4 mr-2" />
                  Left
                </Button>
                <Button variant={section.alignment?.horizontal === "center" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => updateAlignment('horizontal', 'center')}>
                  <AlignCenterHorizontal className="w-4 h-4 mr-2" />
                  Center
                </Button>
                <Button variant={section.alignment?.horizontal === "end" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => updateAlignment('horizontal', 'end')}>
                  <AlignEndHorizontal className="w-4 h-4 mr-2" />
                  Right
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Vertical Alignment</Label>
              <div className="flex gap-2">
                <Button variant={section.alignment?.vertical === "start" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => updateAlignment('vertical', 'start')}>
                  <AlignStartVertical className="w-4 h-4 mr-2" />
                  Top
                </Button>
                <Button variant={section.alignment?.vertical === "center" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => updateAlignment('vertical', 'center')}>
                  <AlignCenterVertical className="w-4 h-4 mr-2" />
                  Middle
                </Button>
                <Button variant={section.alignment?.vertical === "end" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => updateAlignment('vertical', 'end')}>
                  <AlignEndVertical className="w-4 h-4 mr-2" />
                  Bottom
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <SectionBackgroundDialog isOpen={isBackgroundDialogOpen} onOpenChange={setIsBackgroundDialogOpen} background={section.background} onUpdate={background => onUpdate({
      ...section,
      background
    })} />
    </div>;
};